import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import SpellingSuggestion from '../components/SpellingSuggestion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import posthog from 'posthog-js';
import { useAuth } from '../contexts/AuthContext';
import { phrasesApi, practiceApi, translateApi, generateApi, collectionsApi, exportApi, settingsApi } from '../lib/api';
import { parseCSV, SpeechRecognizer, speak, loadVoices, getFrenchVoices, getSavedSpeed, saveSpeed, SPEED_OPTIONS, getSavedVoice, saveVoice, getSavedFlow, saveFlow, flowPath, getSavedOrder, isPhraseTooLong, MAX_RECORDING_MS } from '../lib/speech';
import { speakFrench, getSavedTTSVoice } from '../lib/tts';
import { frenchTypography } from '../lib/utils';
import { markChecklistDone } from '../lib/onboardingChecklist';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import PracticeSessionDialog from '../components/PracticeSessionDialog';
import { FlagFR, FlagGB } from '../components/Flag';
import CollectionSelect, { CollectionOptions, AddCollectionButton } from '../components/CollectionSelect';
import { UpgradeDialog } from '../components/ProUpgrade';
import DictateButton from '../components/DictateButton';
import PremadeCollectionsDialog from '../components/PremadeCollectionsDialog';
import { toast } from 'sonner';
import { Plus, Play, Trash2, Edit2, BarChart3, BookOpen, Target, Upload, Sparkles, Loader2, Folder, FolderOpen, X, Check, CheckSquare, Square, Download, Search, Volume2, Pencil, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Star, LibraryBig } from 'lucide-react';
import { MASTERED_SCORE, getScoreColor } from '../lib/score';

// Most-recently-created collections first, in every dropdown that renders them.
// Falls back to name order when created_at is missing so legacy rows stay stable.
const sortByRecent = (list) => [...(list || [])].sort((a, b) => {
    const ax = a.created_at || '', bx = b.created_at || '';
    if (ax === bx) return (a.name || '').localeCompare(b.name || '');
    return bx > ax ? 1 : -1;
});

// Recording stops at 29 seconds, and the accuracy engine will not score a clip
// longer than that. A phrase nobody can finish inside the window quietly loses
// its acoustic score, which is worth knowing while it can still be shortened
// rather than after a month of practising it.
const TooLongWarning = ({ text, className = '' }) => {
    if (!isPhraseTooLong(text)) return null;
    return (
        <p className={`flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 ${className}`}>
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
            <span>
                This one is long. Recording stops after {Math.round(MAX_RECORDING_MS / 1000)} seconds,
                so if you do not finish in time you will not get an accuracy score for it. Splitting it
                into two shorter phrases works better.
            </span>
        </p>
    );
};

// How far one phrase has come: the first score it ever got against the latest.
//
// The Score is a running mean of every attempt, so a bad first go keeps
// dragging it down long after the mistake has been fixed. The change says the
// thing that average cannot: are you better at this phrase than when you
// started. It needs two scored attempts, since one is a starting point, not
// progress.
//
// All of it lives on the phrase card in the list, behind the Stats toggle, so
// the numbers sit with the phrase they belong to rather than hidden in Edit.
// Total listens = every mode listened. Fall back to legacy listen_count for
// older phrases that predate per-mode counters.
// Total repeats = repeat + record modes, and every scored practice implies at
// least one repeat (Manual mode doesn't tick listen_record_count).
const phraseCounts = (phrase) => {
    const practices = phrase.practice_count || 0;
    const perMode = (phrase.listen_only_count || 0) + (phrase.listen_repeat_count || 0) + (phrase.listen_record_count || 0);
    return {
        practices,
        listens: Math.max(perMode, phrase.listen_count || 0, practices),
        repeats: Math.max((phrase.listen_repeat_count || 0) + (phrase.listen_record_count || 0), practices),
    };
};

// Has this phrase been practised at all yet? A card with nothing to show gets
// no toggle, rather than one that opens onto an empty row.
const hasPhraseStats = (phrase) => {
    const { listens, repeats } = phraseCounts(phrase);
    return listens + repeats > 0 || phrase.average_score != null;
};

// Which cards have their stats open, by phrase id.
const STATS_OPEN_KEY = 'mimique_phrase_stats_open';

const PhraseStats = ({ phrase }) => {
    const { listens, repeats, practices } = phraseCounts(phrase);
    const first = phrase.first_score;
    const last = phrase.last_score;
    const hasProgress = first != null && last != null && practices >= 2;
    const change = hasProgress ? Math.round(last - first) : 0;
    const changeTone = change > 0 ? 'text-emerald-600' : change < 0 ? 'text-rose-600' : 'text-stone-600';

    return (
        <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-stone-500 mt-1">
            <span>Listens <span className="text-stone-700 font-medium">{listens}</span></span>
            <span>Repeats <span className="text-stone-700 font-medium">{repeats}</span></span>
            {phrase.average_score != null && (
                <span>
                    Score{' '}
                    <span className={`font-medium ${getScoreColor(phrase.average_score)}`}>
                        {Math.round(phrase.average_score)}%
                    </span>
                </span>
            )}
            {hasProgress && (
                <span>
                    Progress{' '}
                    <span className={`font-medium ${changeTone}`}>
                        {change > 0 ? '+' : ''}{change}{change !== 0 ? '%' : ' change'}
                    </span>
                </span>
            )}
        </div>
    );
};

const Collections = () => {
    const { token, user } = useAuth();
    const navigate = useNavigate();
    // ?highlight=add-phrase|ai-generate pulsates the matching button until the
    // user clicks it (or another button of the pair), so newcomers coming from
    // the Dashboard checklist can't miss where the action is.
    const [searchParams, setSearchParams] = useSearchParams();
    const highlightTarget = searchParams.get('highlight');
    const clearHighlight = () => {
        if (!searchParams.get('highlight')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('highlight');
        setSearchParams(next, { replace: true });
    };
    const [phrases, setPhrases] = useState([]);
    const [collections, setCollections] = useState([]);
    const [stats, setStats] = useState(null);
    // { title, message } while an upgrade prompt is open, null otherwise.
    const [upgradePrompt, setUpgradePrompt] = useState(null);
    // AI Generate is Pro and above (PLAN_AI_MONTHLY.free is 0 on the server, so
    // a Free user hitting Generate would 402 anyway). Gate the UI too, rather
    // than letting them fill the whole dialog in before being told no.
    const isFree = (user?.plan || 'free') === 'free';
    // No intro line: the shared list under the title already says what the
    // paid plans give them, and saying it twice reads as padding.
    const promptAiUpgrade = () => setUpgradePrompt({
        title: 'AI Generate is a paid feature',
    });
    const [loading, setLoading] = useState(true);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [alternatives, setAlternatives] = useState([]);
    const [loadingAlternatives, setLoadingAlternatives] = useState(false);
    const [showAlternatives, setShowAlternatives] = useState(false);
    const [editTranslating, setEditTranslating] = useState(false);
    const [editReverseTranslating, setEditReverseTranslating] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [premadeDialogOpen, setPremadeDialogOpen] = useState(false);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    const [inlineCreating, setInlineCreating] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [practiceDialogOpen, setPracticeDialogOpen] = useState(false);
    const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [editingPhrase, setEditingPhrase] = useState(null);
    const [selectedCollectionFilter, setSelectedCollectionFilter] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');

    // ?filter=mastered arrives from the Dashboard's Mastered tile. Held in
    // state rather than read straight off the URL so it can be cleared without
    // a navigation, same as the collection filter.
    const [masteredOnly, setMasteredOnly] = useState(
        () => searchParams.get('filter') === 'mastered');
    const clearMasteredOnly = () => {
        setMasteredOnly(false);
        if (searchParams.get('filter')) {
            const next = new URLSearchParams(searchParams);
            next.delete('filter');
            setSearchParams(next, { replace: true });
        }
    };

    // Stats belong to the phrase, so the toggle sits on the card rather than
    // over the whole list: the numbers matter on the one phrase being worked
    // on, not on all ninety at once. Cards left open stay open between visits.
    const [statsOpenIds, setStatsOpenIds] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(STATS_OPEN_KEY) || '[]')); } catch { return new Set(); }
    });
    const toggleStatsFor = (id) => setStatsOpenIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        try { localStorage.setItem(STATS_OPEN_KEY, JSON.stringify([...next])); } catch { /* private mode */ }
        return next;
    });

    const [selectedPhraseIds, setSelectedPhraseIds] = useState([]);
    const [isSelectionMode, setIsSelectionMode] = useState(false);

    const [newPhrase, setNewPhrase] = useState({ french: '', english: '', phonetic: '', collection_ids: [] });
    const [newCollectionName, setNewCollectionName] = useState('');
    const [translating, setTranslating] = useState(false);
    // Spelling suggestions for typed French, { for, text } (SpellingSuggestion).
    const [frenchSuggestion, setFrenchSuggestion] = useState(null);
    const [editFrenchSuggestion, setEditFrenchSuggestion] = useState(null);
    const [inputLang, setInputLang] = useState('english');
    const [translatingToFrench, setTranslatingToFrench] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generateFormality, setGenerateFormality] = useState('informal');
    const [generateGender, setGenerateGender] = useState('male');
    const [generateMode, setGenerateMode] = useState(() => localStorage.getItem('generate_mode') || 'category');
    const [generateCategory, setGenerateCategory] = useState('common');
    const [generateCount, setGenerateCount] = useState(10);
    const [generateExtraContext, setGenerateExtraContext] = useState('');
    const [generateLevel, setGenerateLevel] = useState('beginner');
    const [generateCollectionId, setGenerateCollectionId] = useState('');
    const [generateHistory, setGenerateHistory] = useState([]);
    const [selectedHistoryContext, setSelectedHistoryContext] = useState(null);
    // Session-only highlight for AI-generated phrases — persists through re-renders/re-mounts,
    // cleared on logout or tab close (sessionStorage is tab-scoped)
    const [newPhraseIds, setNewPhraseIds] = useState(new Set());
    const newPhraseStorageLoaded = useRef(false);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renamingCollection, setRenamingCollection] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [importing, setImporting] = useState(false);
    const [importCollectionId, setImportCollectionId] = useState('');
    const [selectedPracticeCollections, setSelectedPracticeCollections] = useState([]);
    const [bulkAssignCollectionId, setBulkAssignCollectionId] = useState('');
    const [exportCollectionId, setExportCollectionId] = useState('');
    const [practiceFlow, setPracticeFlow] = useState(getSavedFlow);
    const [practiceMode, setPracticeMode] = useState('listen-record');
    const [practiceOrder] = useState(getSavedOrder);
    const [playbackSpeed, setPlaybackSpeed] = useState(getSavedSpeed);
    const [playingPhraseId, setPlayingPhraseId] = useState(null);
    const [selectedVoice, setSelectedVoice] = useState(getSavedVoice);
    const [frenchVoices, setFrenchVoices] = useState([]);

    const fileInputRef = useRef(null);
    const translateTimeoutRef = useRef(null);
    const editTranslateTimeoutRef = useRef(null);
    const editEnglishTranslateTimeoutRef = useRef(null);
    const editFrenchRef = useRef(null);
    const editEnglishRef = useRef(null);
    const userIdRef = useRef(user?.id);
    const voiceRecognizerRef = useRef(null);
    const defaultLevelRef = useRef('beginner'); // tracks the level saved in user settings
    const [isVoiceRecording, setIsVoiceRecording] = useState(false);

    // Keep userIdRef current so the unmount cleanup can read it safely
    useEffect(() => { userIdRef.current = user?.id; }, [user?.id]);

    // Clear highlights when navigating away for real (not to practice with returnTo=collections)
    useEffect(() => {
        return () => {
            if (!sessionStorage.getItem('collections_returning_from_practice')) {
                const uid = userIdRef.current;
                if (uid) sessionStorage.removeItem(`new_phrase_ids_${uid}`);
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Load from sessionStorage once user ID is confirmed (avoids race with auth loading)
    useEffect(() => {
        if (!user?.id || newPhraseStorageLoaded.current) return;
        newPhraseStorageLoaded.current = true;
        sessionStorage.removeItem('collections_returning_from_practice');
        try {
            const stored = sessionStorage.getItem(`new_phrase_ids_${user.id}`);
            if (stored) setNewPhraseIds(new Set(JSON.parse(stored)));
        } catch {}
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep sessionStorage in sync so highlights survive component re-mounts within the session
    useEffect(() => {
        if (!user?.id || !newPhraseStorageLoaded.current) return;
        try { sessionStorage.setItem(`new_phrase_ids_${user.id}`, JSON.stringify([...newPhraseIds])); } catch {}
    }, [newPhraseIds, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-resize edit dialog textareas when values change (handles programmatic updates from auto-translate)
    useEffect(() => {
        const el = editFrenchRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
    }, [editingPhrase?.french]);

    useEffect(() => {
        const el = editEnglishRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
    }, [editingPhrase?.english]);

    const [voiceInterim, setVoiceInterim] = useState('');
    // Separate from the Add Phrase dictation above: both dialogs can be
    // open in the same session and must not share a recording flag.
    const [isDescRecording, setIsDescRecording] = useState(false);
    const [descInterim, setDescInterim] = useState('');
    const descRecognizerRef = useRef(null);

    const fetchData = useCallback(async () => {
        try {
            const [phrasesRes, statsRes, collectionsRes] = await Promise.all([
                phrasesApi.getAll(selectedCollectionFilter),
                practiceApi.getStats(),
                collectionsApi.getAll(),
            ]);
            setPhrases(phrasesRes.data);
            setStats(statsRes.data);
            setCollections(sortByRecent(collectionsRes.data));
        } catch (err) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [selectedCollectionFilter]);

    useEffect(() => {
        loadVoices().then(() => setFrenchVoices(getFrenchVoices()));
        fetchData();
        settingsApi.get().then(res => {
            setGenerateFormality(res.data.formality_preference ?? 'informal');
            setGenerateGender(res.data.speaker_gender ?? 'male');
            const level = res.data.french_level ?? 'beginner';
            defaultLevelRef.current = level;
            setGenerateLevel(level);
        }).catch(() => {});
    }, [selectedCollectionFilter, fetchData]);

    useEffect(() => {
        setSelectedPhraseIds([]);
        setIsSelectionMode(false);
    }, [selectedCollectionFilter]);

    // Pre-populate collection when Add or Generate dialogs open
    useEffect(() => {
        if (addDialogOpen) {
            setNewPhrase(p => ({ ...p, collection_ids: selectedCollectionFilter ? [selectedCollectionFilter] : [] }));
        }
    }, [addDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (generateDialogOpen) {
            setGenerateCollectionId(selectedCollectionFilter || '');
            setSelectedHistoryContext(null);
            // Reset to last-known value instantly, then refresh from server
            setGenerateLevel(defaultLevelRef.current);
            settingsApi.get().then(res => {
                const level = res.data.french_level ?? 'beginner';
                defaultLevelRef.current = level;
                setGenerateLevel(level);
            }).catch(() => {});
        }
    }, [generateDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch history scoped to the selected collection — re-runs when dialog opens or collection changes
    useEffect(() => {
        if (!generateDialogOpen) { setGenerateHistory([]); return; }
        setSelectedHistoryContext(null);
        generateApi.getHistory(generateCollectionId || null)
            .then(res => setGenerateHistory(res.data.history || []))
            .catch(() => {});
    }, [generateDialogOpen, generateCollectionId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Closing the dialog mid-sentence must release the microphone. Without this
    // the recogniser keeps listening with nothing on screen to show it.
    useEffect(() => {
        if (generateDialogOpen) return;
        if (descRecognizerRef.current) descRecognizerRef.current.stop();
        setIsDescRecording(false);
        setDescInterim('');
    }, [generateDialogOpen]);

    // Same on unmount, for navigating away while still recording.
    useEffect(() => () => {
        if (descRecognizerRef.current) descRecognizerRef.current.stop();
    }, []);

    const getCollectionNames = (collectionIds) => {
        if (!collectionIds || collectionIds.length === 0) return [];
        return collections.filter(c => collectionIds.includes(c.id)).map(c => c.name);
    };

    const filteredPhrases = useMemo(() => {
        // Same threshold the dashboard counts, so the tile and this list agree.
        const base = masteredOnly
            ? phrases.filter(p => (p.average_score ?? 0) >= MASTERED_SCORE)
            : phrases;
        if (!searchQuery.trim()) return base;
        const query = searchQuery.toLowerCase().trim();
        return base.filter(phrase => {
            if (phrase.french?.toLowerCase().includes(query)) return true;
            if (phrase.english?.toLowerCase().includes(query)) return true;
            if (phrase.phonetic?.toLowerCase().includes(query)) return true;
            const names = collections
                .filter(c => phrase.collection_ids?.includes(c.id))
                .map(c => c.name);
            if (names.some(name => name.toLowerCase().includes(query))) return true;
            return false;
        });
    }, [phrases, searchQuery, collections, masteredOnly]);

    const handleFrenchChange = (value) => {
        setNewPhrase({ ...newPhrase, french: value });
        if (translateTimeoutRef.current) clearTimeout(translateTimeoutRef.current);
        if (value.trim().length > 2) {
            translateTimeoutRef.current = setTimeout(async () => {
                setTranslating(true);
                try {
                    const res = await translateApi.translate(value);
                    setNewPhrase(prev => ({ ...prev, english: res.data.english }));
                    setFrenchSuggestion(res.data.corrected ? { for: value, text: res.data.corrected } : null);
                } catch (err) {
                    console.error('Translation failed:', err);
                } finally {
                    setTranslating(false);
                }
            }, 800);
        }
    };

    const handleEnglishInputChange = (value) => {
        setNewPhrase(prev => ({ ...prev, english: value }));
        if (translateTimeoutRef.current) clearTimeout(translateTimeoutRef.current);
        if (value.trim().length > 2) {
            translateTimeoutRef.current = setTimeout(async () => {
                setTranslatingToFrench(true);
                try {
                    const res = await translateApi.translateToFrench(value);
                    setNewPhrase(prev => ({ ...prev, french: res.data.french, phonetic: res.data.phonetic || prev.phonetic }));
                } catch (err) {
                    console.error('Translation to French failed:', err);
                } finally {
                    setTranslatingToFrench(false);
                }
            }, 800);
        }
    };

    const handleEditFrenchChange = (value) => {
        setEditingPhrase(prev => ({ ...prev, french: value }));
        if (editTranslateTimeoutRef.current) clearTimeout(editTranslateTimeoutRef.current);
        if (value.trim().length > 2) {
            editTranslateTimeoutRef.current = setTimeout(async () => {
                setEditTranslating(true);
                try {
                    const res = await translateApi.translatePhrase(value);
                    setEditingPhrase(prev => ({ ...prev, english: res.data.english, phonetic: res.data.phonetic }));
                    setEditFrenchSuggestion(res.data.corrected ? { for: value, text: res.data.corrected } : null);
                } catch (err) {
                    console.error('Edit translation failed:', err);
                } finally {
                    setEditTranslating(false);
                }
            }, 600);
        }
    };

    const handleEditEnglishChange = (value) => {
        setEditingPhrase(prev => ({ ...prev, english: value }));
        if (editEnglishTranslateTimeoutRef.current) clearTimeout(editEnglishTranslateTimeoutRef.current);
        if (value.trim().length > 2) {
            editEnglishTranslateTimeoutRef.current = setTimeout(async () => {
                setEditReverseTranslating(true);
                try {
                    const res = await translateApi.translateToFrench(value);
                    setEditingPhrase(prev => ({ ...prev, french: res.data.french, phonetic: res.data.phonetic || prev.phonetic }));
                } catch (err) {
                    console.error('Edit reverse translation failed:', err);
                } finally {
                    setEditReverseTranslating(false);
                }
            }, 600);
        }
    };

    const handleStartVoiceInput = () => {
        setVoiceInterim('');
        const isEnglish = inputLang === 'english';
        voiceRecognizerRef.current = new SpeechRecognizer(
            (text) => {
                if (isEnglish) handleEnglishInputChange(text);
                else handleFrenchChange(text);
                setIsVoiceRecording(false);
                setVoiceInterim('');
            },
            (text) => { setVoiceInterim(text); },
            isEnglish ? 'en-GB' : 'fr-FR'
        );
        if (!voiceRecognizerRef.current.isSupported()) {
            toast.error('Speech recognition not supported in this browser');
            return;
        }
        voiceRecognizerRef.current.start();
        setIsVoiceRecording(true);
    };

    const handleStopVoiceInput = () => {
        if (voiceRecognizerRef.current) voiceRecognizerRef.current.stop();
        setIsVoiceRecording(false);
        setVoiceInterim('');
    };

    // Dictating the AI Generate description. Always English, since that is the
    // language the description is written in, whatever the phrases come out as.
    // Continuous, because people describe what they want in more than one
    // breath, and appended rather than replaced so a pause does not wipe what
    // they already said or typed.
    const handleStartDescDictation = () => {
        setDescInterim('');
        const baseline = generateExtraContext.trim();
        descRecognizerRef.current = new SpeechRecognizer(
            null,
            null,
            'en-GB',
            {
                continuous: true,
                onTranscript: (heard) => {
                    setGenerateExtraContext(baseline && heard ? `${baseline} ${heard}` : baseline || heard);
                },
                onError: (err) => {
                    setIsDescRecording(false);
                    setDescInterim('');
                    if (err === 'not-allowed' || err === 'service-not-allowed') {
                        toast.error('Microphone blocked. Allow mic access for mimique.app in your browser settings.');
                    } else if (err !== 'aborted' && err !== 'no-speech') {
                        toast.error('Could not hear that. Try again, or type it instead.');
                    }
                },
                onEnd: () => { setIsDescRecording(false); setDescInterim(''); },
            }
        );
        if (!descRecognizerRef.current.isSupported()) {
            toast.error('Speech recognition not supported in this browser. Try Chrome, or type it instead.');
            return;
        }
        descRecognizerRef.current.start();
        setIsDescRecording(true);
    };

    const handleStopDescDictation = () => {
        if (descRecognizerRef.current) descRecognizerRef.current.stop();
        setIsDescRecording(false);
        setDescInterim('');
    };

    const handleSpeedChange = (speed) => { setPlaybackSpeed(speed); saveSpeed(speed); };
    const handleVoiceChange = (voiceURI) => { setSelectedVoice(voiceURI); saveVoice(voiceURI); };

    const handlePlayPhrase = async (phraseId, frenchText) => {
        if (playingPhraseId) return;
        setPlayingPhraseId(phraseId);
        try {
            await speakFrench(frenchText, playbackSpeed, getSavedTTSVoice());
            posthog.capture('phrase_listened', { phrase_id: phraseId, source: 'collections' });
        } catch (err) {
            toast.error('Voice playback unavailable. Try again, or install a French voice in your device settings.');
        } finally {
            setPlayingPhraseId(null);
        }
    };

    const handleAddPhrase = async (e) => {
        e.preventDefault();
        const duplicate = phrases.some(p => p.french.trim().toLowerCase() === newPhrase.french.trim().toLowerCase());
        if (duplicate) { toast.error('This phrase already exists in your list'); return; }
        try {
            const res = await phrasesApi.create(newPhrase);
            setPhrases([res.data, ...phrases]);
            setNewPhrase({ french: '', english: '', phonetic: '', collection_ids: [] });
            setAddDialogOpen(false);
            toast.success('Phrase added!');
            markChecklistDone(user?.id, 'add_phrase');
        } catch (err) {
            // 402 is the plan cap. The server's `detail` already names the cap
            // and the upgrade path, so show that rather than inventing our own
            // wording that would drift from PLAN_PHRASE_TOTAL.
            const detail = err.response?.data?.detail;
            if (err.response?.status === 402) {
                setAddDialogOpen(false);
                setUpgradePrompt({ title: "You've run out of phrase slots", message: detail });
                return;
            }
            toast.error(detail || 'Failed to add phrase');
        }
    };

    const handleUpdatePhrase = async (e) => {
        e.preventDefault();
        if (editTranslateTimeoutRef.current) clearTimeout(editTranslateTimeoutRef.current);
        if (editEnglishTranslateTimeoutRef.current) clearTimeout(editEnglishTranslateTimeoutRef.current);
        setEditSaving(true);
        try {
            const res = await phrasesApi.update(editingPhrase.id, {
                french: editingPhrase.french,
                english: editingPhrase.english,
                phonetic: editingPhrase.phonetic,
                collection_ids: editingPhrase.collection_ids || [],
            });
            setPhrases(phrases.map(p => p.id === editingPhrase.id ? res.data : p));
            setEditDialogOpen(false);
            setEditingPhrase(null);
            setAlternatives([]);
            setShowAlternatives(false);
            toast.success('Phrase updated!');
            fetchData();
        } catch (err) {
            toast.error('Failed to update phrase');
        } finally {
            setEditSaving(false);
        }
    };

    const handleGetAlternatives = async () => {
        setAlternatives([]);
        setShowAlternatives(true);
        setLoadingAlternatives(true);
        try {
            const res = await phrasesApi.getAlternatives(editingPhrase.id);
            setAlternatives(res.data.alternatives || []);
        } catch (err) {
            toast.error('Failed to generate alternatives');
            setShowAlternatives(false);
        } finally {
            setLoadingAlternatives(false);
        }
    };

    const handleSelectAlternative = (alt) => {
        setEditingPhrase(prev => ({ ...prev, french: alt.french, english: alt.english, phonetic: alt.phonetic || prev.phonetic }));
        setShowAlternatives(false);
        setAlternatives([]);
    };

    const handleDeletePhrase = async (id) => {
        if (!window.confirm('Delete this phrase?')) return;
        try {
            await phrasesApi.delete(id);
            setPhrases(phrases.filter(p => p.id !== id));
            setSelectedPhraseIds(selectedPhraseIds.filter(pid => pid !== id));
            toast.success('Phrase deleted');
            // The header count and the "All phrases (n)" option both read
            // stats.total_phrases, not phrases.length, so they stay stale
            // without this. Bulk delete already did it; single delete did not.
            fetchData();
        } catch (err) {
            toast.error('Failed to delete phrase');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedPhraseIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedPhraseIds.length} selected phrase(s)?`)) return;
        try {
            await Promise.all(selectedPhraseIds.map(id => phrasesApi.delete(id)));
            setPhrases(phrases.filter(p => !selectedPhraseIds.includes(p.id)));
            setSelectedPhraseIds([]);
            setIsSelectionMode(false);
            toast.success(`Deleted ${selectedPhraseIds.length} phrases`);
            fetchData();
        } catch (err) {
            toast.error('Failed to delete some phrases');
        }
    };

    const handleBulkAssign = async () => {
        if (selectedPhraseIds.length === 0) return;
        try {
            await Promise.all(selectedPhraseIds.map(id =>
                phrasesApi.update(id, { collection_ids: bulkAssignCollectionId ? [bulkAssignCollectionId] : [] })
            ));
            setBulkAssignDialogOpen(false);
            setBulkAssignCollectionId('');
            setSelectedPhraseIds([]);
            setIsSelectionMode(false);
            toast.success(`Updated ${selectedPhraseIds.length} phrases`);
            fetchData();
        } catch (err) {
            toast.error('Failed to update some phrases');
        }
    };

    const togglePhraseSelection = (phraseId) => {
        if (selectedPhraseIds.includes(phraseId)) {
            setSelectedPhraseIds(selectedPhraseIds.filter(id => id !== phraseId));
        } else {
            setSelectedPhraseIds([...selectedPhraseIds, phraseId]);
        }
    };

    const toggleSelectAll = () => {
        const filteredIds = filteredPhrases.map(p => p.id);
        if (filteredIds.every(id => selectedPhraseIds.includes(id))) {
            setSelectedPhraseIds(selectedPhraseIds.filter(id => !filteredIds.includes(id)));
        } else {
            setSelectedPhraseIds([...new Set([...selectedPhraseIds, ...filteredIds])]);
        }
    };

    const createCollectionRaw = async (name) => {
        const res = await collectionsApi.create(name);
        setCollections(prev => sortByRecent([...prev, res.data]));
        return res.data;
    };
    const createCollection = async () => {
        if (!newCollectionName.trim()) return;
        try {
            const c = await createCollectionRaw(newCollectionName);
            setNewCollectionName('');
            setInlineCreating(false);
            setSelectedCollectionFilter(c.id);
            setImportCollectionId(c.id);
            toast.success('Collection created!');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create collection');
        }
    };
    // Create a collection (with toast) and return it, or null on failure.
    // Used by the shared CollectionSelect's inline "Add collection".
    const createCollectionToast = async (name) => {
        try {
            const c = await createCollectionRaw(name);
            toast.success('Collection created!');
            return c;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create collection');
            return null;
        }
    };

    const handleDeleteCollection = async (id) => {
        if (!window.confirm('Delete this collection and all its phrases? This cannot be undone.')) return;
        try {
            await collectionsApi.delete(id);
            setCollections(collections.filter(c => c.id !== id));
            setPhrases(phrases.filter(p => !p.collection_ids.includes(id)));
            if (selectedCollectionFilter === id) setSelectedCollectionFilter(null);
            toast.success('Collection and phrases deleted');
            fetchData();
        } catch (err) {
            toast.error('Failed to delete collection');
        }
    };

    const handleRenameCollection = async (e) => {
        e.preventDefault();
        if (!renameValue.trim() || !renamingCollection) return;
        try {
            const res = await collectionsApi.update(renamingCollection.id, renameValue.trim());
            setCollections(collections.map(c => c.id === renamingCollection.id ? { ...c, name: res.data.name } : c));
            setRenameDialogOpen(false);
            setRenamingCollection(null);
            setRenameValue('');
            toast.success('Collection renamed');
        } catch (err) {
            toast.error('Failed to rename collection');
        }
    };

    const handleExportCSV = () => {
        const exportUrl = exportApi.exportCSV(exportCollectionId || null);
        fetch(exportUrl, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(res => res.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = exportCollectionId
                    ? `${collections.find(c => c.id === exportCollectionId)?.name || 'collection'}_export.csv`
                    : 'phrases_export.csv';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
                setExportDialogOpen(false);
                toast.success('Export downloaded!');
            })
            .catch(() => toast.error('Export failed'));
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        try {
            const content = await file.text();
            const parsedPhrases = parseCSV(content);
            if (parsedPhrases.length === 0) { toast.error('No valid phrases found in CSV'); return; }
            const existingFrench = new Set(phrases.map(p => p.french.trim().toLowerCase()));
            const phrasesToCreate = [];
            let skipped = 0;
            for (const p of parsedPhrases) {
                if (existingFrench.has(p.french.trim().toLowerCase())) { skipped++; continue; }
                if (!p.english && p.french) {
                    try { const res = await translateApi.translate(p.french); p.english = res.data.english; }
                    catch (err) { p.english = ''; }
                }
                if (importCollectionId) p.collection_ids = [importCollectionId];
                phrasesToCreate.push(p);
            }
            if (phrasesToCreate.length === 0) { toast.error('All phrases in this file already exist'); return; }
            const res = await phrasesApi.createBulk(phrasesToCreate);
            setPhrases([...res.data, ...phrases]);
            setImportDialogOpen(false);
            setImportCollectionId('');
            const skippedMsg = skipped > 0 ? ` (${skipped} duplicate${skipped > 1 ? 's' : ''} skipped)` : '';
            toast.success(`Imported ${res.data.length} phrases!${skippedMsg}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to import CSV');
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleGeneratePhrases = async () => {
        // In custom mode, require either typed context or a selected past context
        if (generateMode === 'custom' && !generateExtraContext.trim() && !selectedHistoryContext) {
            toast.error('Please describe what you want, or select a past context above.');
            return;
        }
        setGenerating(true);
        try {
            const category = generateMode === 'custom' ? null : generateCategory;
            const baseContext = generateMode === 'custom' ? generateExtraContext : '';
            const context = baseContext
                ? `Level: ${generateLevel}\n${baseContext}`
                : `Level: ${generateLevel}`;
            const res = await generateApi.generatePhrases(category, generateCount, context, generateCollectionId ? [generateCollectionId] : [], generateFormality, generateGender, selectedHistoryContext);
            const { phrases: newPhrases, dupes_skipped: dupeCount = 0, capped_by_limit: cappedByLimit = false } = res.data;
            setPhrases([...newPhrases, ...phrases]);
            if (newPhrases.length > 0) {
                setNewPhraseIds(prev => new Set([...prev, ...newPhrases.map(p => p.id)]));
            }
            setGenerateDialogOpen(false);
            setGenerateExtraContext('');
            setGenerateCollectionId('');
            setSelectedHistoryContext(null);
            if (newPhrases.length === 0) {
                toast.warning(`All ${dupeCount} generated phrases already exist — try a more specific prompt or a different level.`);
            } else if (cappedByLimit) {
                toast.warning(`Added ${newPhrases.length} phrase${newPhrases.length !== 1 ? 's' : ''} — collection is now full (100-phrase limit reached).`);
            } else {
                const skippedMsg = dupeCount > 0 ? ` (${dupeCount} duplicate${dupeCount > 1 ? 's' : ''} skipped)` : '';
                toast.success(`Generated ${newPhrases.length} phrases!${skippedMsg}`);
                markChecklistDone(user?.id, 'ai_generate');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to generate phrases');
        } finally {
            setGenerating(false);
        }
    };

    // Practise only the ticked phrases, straight away, in the learner's saved
    // flow (Manual or Automatic). Both read phrase_ids, so it is the same call
    // for either; the list follows the page order unless their order setting is
    // random.
    const handlePracticeSelected = () => {
        const chosen = filteredPhrases.filter(p => selectedPhraseIds.includes(p.id)).map(p => p.id);
        if (chosen.length === 0) return;
        if (practiceOrder === 'random') chosen.sort(() => Math.random() - 0.5);
        sessionStorage.setItem('collections_returning_from_practice', '1');
        saveFlow(practiceFlow);
        const params = new URLSearchParams();
        params.set('phrase_ids', chosen.join(','));
        params.set('label', 'Your chosen phrases');
        if (practiceFlow === 'automatic') params.set('mode', practiceMode);
        params.set('returnTo', 'collections');
        navigate(`${flowPath(practiceFlow)}?${params.toString()}`);
    };

    const handlePracticeWithSelection = () => {
        sessionStorage.setItem('collections_returning_from_practice', '1');
        saveFlow(practiceFlow);
        const params = new URLSearchParams();
        params.set('order', practiceOrder);
        // Mode only applies to the automatic hands-free loop.
        if (practiceFlow === 'automatic') params.set('mode', practiceMode);
        if (selectedPracticeCollections.length > 0) params.set('collections', selectedPracticeCollections.join(','));
        params.set('returnTo', 'collections');
        navigate(`${flowPath(practiceFlow)}?${params.toString()}`);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-stone-600">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-fixed bg-gradient-to-b from-foreground to-primary text-primary-foreground">
            {/* Not sticky: this header carries the title, search, collection
                filter and every action button, so it can end up taller than the
                viewport — and a sticky box taller than the scrollport can't pin,
                it just scrolls away. Selecting a collection adds the rename and
                delete buttons, which was enough to tip it over on smaller
                screens and make the whole header vanish. */}
            <header className="relative z-40 bg-foreground/90 backdrop-blur-md border-b border-primary-foreground/10">
                <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
                    <h1 className="text-xl font-bold text-primary-foreground">Manage Phrases</h1>
                    <p className="text-sm text-primary-foreground/70 truncate">{stats?.total_phrases || 0} phrases across {collections.length} collection{collections.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="max-w-6xl mx-auto px-4 md:px-6 pb-4">
                {/* Search + folder filter */}
                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
                    {stats?.total_phrases > 0 && (
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
                            <Input
                                data-testid="search-input"
                                type="text"
                                placeholder="Search phrases"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 h-8 text-xs bg-white text-foreground placeholder:text-stone-400 border-stone-200"
                            />
                            {searchQuery && (
                                <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 text-stone-400 hover:text-primary" onClick={() => setSearchQuery('')}>
                                    <X className="h-3.5 w-3.5" />
                                </Button>
                            )}
                        </div>
                    )}
                    <div className="flex items-center gap-1.5 w-full md:flex-1">
                        {inlineCreating ? (
                            /* Name field lives OUTSIDE the Select — typing inside a Radix
                               Select dropdown breaks on Android's keyboard. */
                            <div className="flex items-center gap-1.5 flex-1">
                                <Input
                                    autoFocus
                                    data-testid="inline-collection-name-input"
                                    value={newCollectionName}
                                    onChange={e => setNewCollectionName(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createCollection(); } }}
                                    placeholder="New collection name"
                                    className="flex-1 h-8 text-xs bg-white text-foreground border-stone-200"
                                />
                                <Button type="button" size="sm" data-testid="inline-create-collection-btn" className="h-8 rounded-md bg-primary hover:bg-primary/80 shrink-0" onClick={createCollection}>
                                    Create
                                </Button>
                                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-primary-foreground/60 hover:text-primary-foreground hover:bg-primary-foreground/10 shrink-0" onClick={() => { setInlineCreating(false); setNewCollectionName(''); }}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        ) : (
                            <>
                                {/* value: null means "no filter", shown as the All Phrases
                                    option rather than falling back to the placeholder —
                                    e.g. after deleting the selected collection. */}
                                <Select
                                    open={filterOpen}
                                    onOpenChange={setFilterOpen}
                                    value={selectedCollectionFilter || 'all'}
                                    onValueChange={v => setSelectedCollectionFilter(v === 'all' ? null : v)}
                                >
                                    <SelectTrigger data-testid="collection-filter-select" className="flex-1 h-8 text-xs bg-white text-foreground border-0 shadow-sm data-[placeholder]:text-stone-400">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <Folder className="h-3.5 w-3.5 text-stone-400 shrink-0" />
                                            <SelectValue placeholder="Select Collection" />
                                        </div>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Phrases</SelectItem>
                                        <CollectionOptions>
                                            {collections.map(c => (
                                                <SelectItem key={c.id} value={c.id} className="group">
                                                    <div className="flex items-center justify-between gap-3 w-full">
                                                        <span>{c.name}</span>
                                                        <span className="ml-auto text-xs text-stone-400 group-data-[highlighted]:text-accent-foreground">{c.phrase_count}</span>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </CollectionOptions>
                                        <AddCollectionButton
                                            testId="inline-add-collection-btn"
                                            onClick={() => { setFilterOpen(false); setNewCollectionName(''); setInlineCreating(true); }}
                                        />
                                    </SelectContent>
                                </Select>

                                {selectedCollectionFilter && collections.find(c => c.id === selectedCollectionFilter) && (
                                    <>
                                        <Button
                                            data-testid={`rename-collection-${selectedCollectionFilter}`}
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-primary-foreground/60 hover:text-primary-foreground hover:bg-primary-foreground/10"
                                            onClick={() => {
                                                const coll = collections.find(c => c.id === selectedCollectionFilter);
                                                setRenamingCollection(coll);
                                                setRenameValue(coll.name);
                                                setRenameDialogOpen(true);
                                            }}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            data-testid={`delete-collection-${selectedCollectionFilter}`}
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-rose-400 hover:text-rose-300 hover:bg-primary-foreground/10"
                                            onClick={() => handleDeleteCollection(selectedCollectionFilter)}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="flex justify-start mb-4">
                <div className="flex flex-wrap gap-2">
                    <div className="flex gap-2 shrink-0">
                    <Dialog open={addDialogOpen} onOpenChange={(open) => { setAddDialogOpen(open); if (!open) { setInputLang('english'); setNewPhrase({ french: '', english: '', phonetic: '', collection_ids: [] }); } }}>
                        <DialogTrigger asChild>
                            <Button
                                data-testid="add-phrase-btn"
                                size="sm"
                                onClick={clearHighlight}
                                className={`rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold ${
                                    highlightTarget === 'add-phrase' ? 'animate-onboarding-glow' : ''
                                }`}
                            >
                                <Plus className="h-4 w-4 mr-1" />Add Phrase
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" aria-describedby="add-phrase-description">
                            <DialogHeader><DialogTitle>Add New Phrase</DialogTitle></DialogHeader>
                            <p id="add-phrase-description" className="text-sm text-stone-500 mb-4">Enter a phrase, the translation and phonetics will be auto-generated.</p>
                            <form onSubmit={handleAddPhrase} className="space-y-4">
                                <div className="flex gap-2 p-1 bg-stone-100 rounded-lg">
                                    <button
                                        type="button"
                                        className={`flex-1 inline-flex items-center justify-center gap-2 py-1.5 text-sm font-medium rounded-md transition-all ${inputLang === 'english' ? 'bg-white shadow-sm text-primary' : 'text-stone-500 hover:text-stone-700'}`}
                                        onClick={() => { setInputLang('english'); setNewPhrase({ french: '', english: '', phonetic: '', collection_ids: newPhrase.collection_ids }); }}
                                    >
                                        <FlagGB /> English
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex-1 inline-flex items-center justify-center gap-2 py-1.5 text-sm font-medium rounded-md transition-all ${inputLang === 'french' ? 'bg-white shadow-sm text-primary' : 'text-stone-500 hover:text-stone-700'}`}
                                        onClick={() => { setInputLang('french'); setNewPhrase({ french: '', english: '', phonetic: '', collection_ids: newPhrase.collection_ids }); }}
                                    >
                                        <FlagFR /> French
                                    </button>
                                </div>

                                {inputLang === 'french' ? (
                                    <>
                                        <div className="space-y-2">
                                            <Label htmlFor="french">French Phrase *</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    data-testid="add-phrase-french"
                                                    id="french"
                                                    placeholder={isVoiceRecording ? (voiceInterim || 'Listening...') : 'Bonjour, comment allez-vous?'}
                                                    value={newPhrase.french}
                                                    onChange={(e) => handleFrenchChange(e.target.value)}
                                                    required
                                                    className="bg-stone-50 flex-1"
                                                    disabled={isVoiceRecording}
                                                />
                                                <DictateButton
                                                    testId="voice-input-btn"
                                                    recording={isVoiceRecording}
                                                    onStart={handleStartVoiceInput}
                                                    onStop={handleStopVoiceInput}
                                                    label="Speak the phrase"
                                                />
                                            </div>
                                            {isVoiceRecording && voiceInterim && (
                                                <p className="text-sm text-stone-400 italic" data-testid="voice-interim-text">{voiceInterim}</p>
                                            )}
                                            <SpellingSuggestion suggestion={frenchSuggestion} current={newPhrase.french}
                                                onAccept={(text) => { setNewPhrase(prev => ({ ...prev, french: text })); setFrenchSuggestion(null); }} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="english" className="flex items-center gap-2">
                                                English Translation
                                                {translating && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                                            </Label>
                                            <Input
                                                data-testid="add-phrase-english"
                                                id="english"
                                                placeholder="Auto-translated..."
                                                value={newPhrase.english}
                                                onChange={(e) => setNewPhrase({...newPhrase, english: e.target.value})}
                                                className="bg-stone-50"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="space-y-2">
                                            <Label htmlFor="english-input">English Phrase *</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    data-testid="add-phrase-english-input"
                                                    id="english-input"
                                                    placeholder={isVoiceRecording ? (voiceInterim || 'Listening...') : 'e.g. How are you?'}
                                                    value={newPhrase.english}
                                                    onChange={(e) => handleEnglishInputChange(e.target.value)}
                                                    required
                                                    className="bg-stone-50 flex-1"
                                                    disabled={isVoiceRecording}
                                                />
                                                <DictateButton
                                                    testId="voice-input-btn-en"
                                                    recording={isVoiceRecording}
                                                    onStart={handleStartVoiceInput}
                                                    onStop={handleStopVoiceInput}
                                                    label="Speak the phrase"
                                                />
                                            </div>
                                            {isVoiceRecording && voiceInterim && (
                                                <p className="text-sm text-stone-400 italic">{voiceInterim}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="french-result" className="flex items-center gap-2">
                                                French Translation
                                                {translatingToFrench && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                                            </Label>
                                            <Input
                                                data-testid="add-phrase-french-result"
                                                id="french-result"
                                                placeholder="Auto-translated..."
                                                value={newPhrase.french}
                                                onChange={(e) => setNewPhrase({...newPhrase, french: e.target.value})}
                                                required
                                                className="bg-stone-50"
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="space-y-2">
                                    <Label>Add to Collection</Label>
                                    <CollectionSelect
                                        testId="phrase-collection"
                                        value={newPhrase.collection_ids[0] || ''}
                                        onChange={(id) => setNewPhrase({ ...newPhrase, collection_ids: id ? [id] : [] })}
                                        collections={collections}
                                        onCreateCollection={createCollectionToast}
                                    />
                                </div>
                                <TooLongWarning text={newPhrase.french} className="mb-3" />
                                <Button
                                    data-testid="save-phrase-btn"
                                    type="submit"
                                    className="w-full rounded-full bg-primary hover:bg-primary/80"
                                    disabled={!newPhrase.french || translating}
                                >
                                    Add Phrase
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
                        {/* Same button for everyone — the lock badge told people
                            "no" before they'd even considered it. Free still can't
                            get in: preventDefault stops the dialog opening and shows
                            the upgrade prompt instead, so the paid-only part only
                            shows up once they've actually gone for it. */}
                        <DialogTrigger asChild>
                            <Button
                                data-testid="ai-generate-btn"
                                size="sm"
                                onClick={(e) => {
                                    clearHighlight();
                                    if (isFree) { e.preventDefault(); promptAiUpgrade(); }
                                }}
                                className={`rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold ${
                                    highlightTarget === 'ai-generate' ? 'animate-onboarding-glow' : ''
                                }`}
                            >
                                <Sparkles className="h-4 w-4 mr-1" />AI Generate
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
                            <DialogHeader><DialogTitle>AI Generate Phrases</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div className="flex gap-2 p-1 bg-stone-100 rounded-lg">
                                    <button
                                        type="button"
                                        className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${generateMode === 'category' ? 'bg-white shadow-sm text-primary' : 'text-stone-500 hover:text-stone-700'}`}
                                        onClick={() => { setGenerateMode('category'); localStorage.setItem('generate_mode', 'category'); setSelectedHistoryContext(null); }}
                                    >
                                        By category
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${generateMode === 'custom' ? 'bg-white shadow-sm text-primary' : 'text-stone-500 hover:text-stone-700'}`}
                                        onClick={() => { setGenerateMode('custom'); localStorage.setItem('generate_mode', 'custom'); }}
                                    >
                                        By description
                                    </button>
                                </div>

                                {generateMode === 'category' ? (
                                    <div className="space-y-2">
                                        <Label>Category</Label>
                                        <Select value={generateCategory} onValueChange={setGenerateCategory}>
                                            <SelectTrigger data-testid="category-select" className="bg-stone-50"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="common">Common Phrases</SelectItem>
                                                <SelectItem value="greetings">Greetings & Farewells</SelectItem>
                                                <SelectItem value="restaurant">Restaurant & Food</SelectItem>
                                                <SelectItem value="travel">Travel & Directions</SelectItem>
                                                <SelectItem value="shopping">Shopping & Numbers</SelectItem>
                                                <SelectItem value="emergency">Emergency & Help</SelectItem>
                                                <SelectItem value="tongue_twisters">Tongue Twisters</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {generateHistory.length > 0 && (
                                            <div className="space-y-2">
                                                <Label>Past Context</Label>
                                                <Select
                                                    value={generateHistory.findIndex(e => e.custom_text === selectedHistoryContext) >= 0
                                                        ? String(generateHistory.findIndex(e => e.custom_text === selectedHistoryContext))
                                                        : 'none'}
                                                    onValueChange={(val) => setSelectedHistoryContext(val === 'none' ? null : generateHistory[parseInt(val)].custom_text)}
                                                >
                                                    <SelectTrigger className="bg-stone-50 w-full">
                                                        <SelectValue placeholder="None" />
                                                    </SelectTrigger>
                                                    <SelectContent className="w-[var(--radix-select-trigger-width)]">
                                                        <SelectItem value="none">None</SelectItem>
                                                        {generateHistory.map((entry, i) => {
                                                            const title = entry.title || (
                                                                entry.custom_text.length > 50
                                                                    ? entry.custom_text.slice(0, entry.custom_text.lastIndexOf(' ', 50) || 50) + '…'
                                                                    : entry.custom_text
                                                            );
                                                            return <SelectItem key={i} value={String(i)}>{title}</SelectItem>;
                                                        })}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <Label>Give details of types of phrases</Label>
                                                <span className="text-xs text-stone-400">Type it, or speak it in English</span>
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <textarea
                                                    data-testid="generate-extra-context"
                                                    placeholder={isDescRecording
                                                        ? (descInterim || 'Listening…')
                                                        : (selectedHistoryContext
                                                            ? 'Leave blank to continue, or type a new focus for this batch…'
                                                            : 'e.g. "Gen Z slang used at a football matches or when talking about football" or "Ordering food and asking questions in a normandy restaurant" or "most common phrases using subjunctive"')}
                                                    value={generateExtraContext}
                                                    onChange={(e) => setGenerateExtraContext(e.target.value)}
                                                    className="flex-1 p-2 border border-stone-200 rounded-md bg-stone-50 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                                                    rows={3}
                                                />
                                                <DictateButton
                                                    testId="describe-voice-btn"
                                                    recording={isDescRecording}
                                                    onStart={handleStartDescDictation}
                                                    onStop={handleStopDescDictation}
                                                    label="Speak your description"
                                                />
                                            </div>
                                            {isDescRecording && (
                                                <p className="text-sm text-stone-400 italic" data-testid="describe-interim-text">
                                                    {descInterim || 'Listening… tap the mic again when you have finished.'}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label>Add to Collection <span className="text-stone-400 font-normal">(optional)</span></Label>
                                    <CollectionSelect
                                        testId="generate-collection"
                                        value={generateCollectionId}
                                        onChange={setGenerateCollectionId}
                                        collections={collections}
                                        onCreateCollection={createCollectionToast}
                                    />
                                </div>

                                <div className="flex items-center gap-4">
                                    <Label className="shrink-0 w-20">No. Phrases</Label>
                                    <Select value={generateCount.toString()} onValueChange={(v) => setGenerateCount(parseInt(v))}>
                                        <SelectTrigger data-testid="count-select" className="bg-stone-50 flex-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="1">1 phrase</SelectItem>
                                            <SelectItem value="5">5 phrases</SelectItem>
                                            <SelectItem value="10">10 phrases</SelectItem>
                                            <SelectItem value="15">15 phrases</SelectItem>
                                            <SelectItem value="20">20 phrases</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex items-center gap-4">
                                    <Label className="shrink-0 w-20">Style</Label>
                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                        <input type="radio" name="generateFormality" value="informal" checked={generateFormality === 'informal'} onChange={() => setGenerateFormality('informal')} className="accent-primary" />
                                        <span className="text-sm text-stone-700">Informal</span>
                                    </label>
                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                        <input type="radio" name="generateFormality" value="formal" checked={generateFormality === 'formal'} onChange={() => setGenerateFormality('formal')} className="accent-primary" />
                                        <span className="text-sm text-stone-700">Formal</span>
                                    </label>
                                </div>
                                <Button
                                    data-testid="generate-phrases-btn"
                                    className="w-full rounded-full bg-primary hover:bg-primary/80"
                                    onClick={handleGeneratePhrases}
                                    disabled={generating}
                                >
                                    {generating ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" />Generating...</>) : (<><Sparkles className="h-4 w-4 mr-1" />Generate Phrases</>)}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                    </div>
                    <div className="flex flex-wrap gap-2 basis-full sm:basis-auto">
                    <Button
                        data-testid="premade-collections-btn"
                        size="sm"
                        onClick={() => setPremadeDialogOpen(true)}
                        className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                    >
                        <LibraryBig className="h-4 w-4 mr-1" />Pre-made collections
                    </Button>
                    <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="import-csv-btn" variant="outline" size="sm" className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                                <Upload className="h-4 w-4 mr-1" />Import
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" aria-describedby="import-description">
                            <DialogHeader><DialogTitle>Import Phrases from file</DialogTitle></DialogHeader>
                            <p id="import-description" className="text-sm text-stone-500 mb-4">Upload a CSV file with French phrases. English translations will be auto-generated if not provided.</p>
                            <div className="space-y-4">
                                <div className="p-4 bg-stone-50 rounded-lg text-sm">
                                    <p className="font-medium mb-2">CSV Format:</p>
                                    <code className="text-xs block bg-stone-100 p-2 rounded">
                                        french,english,phonetic<br/>
                                        "Bonjour","Hello","bɔ̃ʒuʁ"<br/>
                                        "Merci"<br/>
                                        "Au revoir","Goodbye"
                                    </code>
                                    <p className="text-stone-500 mt-2">Only French column is required</p>
                                </div>
                                <div className="space-y-2">
                                    <Label>Assign to Collection (Optional)</Label>
                                    <CollectionSelect
                                        testId="import-collection"
                                        value={importCollectionId}
                                        onChange={setImportCollectionId}
                                        collections={collections}
                                        onCreateCollection={createCollectionToast}
                                    />
                                </div>
                                <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleFileSelect} className="hidden" data-testid="csv-file-input" />
                                <Button
                                    data-testid="select-csv-btn"
                                    className="w-full rounded-full bg-primary hover:bg-primary/80"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={importing}
                                >
                                    {importing ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" />Importing & Translating...</>) : (<><Upload className="h-4 w-4 mr-1" />Select CSV File</>)}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                data-testid="export-csv-btn"
                                variant="outline"
                                size="sm"
                                className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                                disabled={phrases.length === 0}
                            >
                                <Download className="h-4 w-4 mr-1" />Export
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" aria-describedby="export-description">
                            <DialogHeader><DialogTitle>Export Phrases to file</DialogTitle></DialogHeader>
                            <p id="export-description" className="text-sm text-stone-500 mb-4">Download your phrases as a CSV file for backup or sharing.</p>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label>What to export</Label>
                                    <Select value={exportCollectionId || 'all'} onValueChange={(v) => setExportCollectionId(v === 'all' ? '' : v)}>
                                        <SelectTrigger data-testid="export-collection-select" className="bg-stone-50">
                                            <SelectValue placeholder="All phrases" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All phrases ({stats?.total_phrases || 0})</SelectItem>
                                            <CollectionOptions>
                                                {collections.map(c => (
                                                    <SelectItem key={c.id} value={c.id}>
                                                        <div className="flex items-center gap-2"><FolderOpen className="h-4 w-4" />{c.name} ({c.phrase_count})</div>
                                                    </SelectItem>
                                                ))}
                                            </CollectionOptions>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button data-testid="download-export-btn" className="w-full rounded-full bg-primary hover:bg-primary/80" onClick={handleExportCSV}>
                                    <Download className="h-4 w-4 mr-1" />Download CSV
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                    </div>
                </div>
                </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
                {/* Phrases List */}
                <div>
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <h2 className="text-lg font-semibold text-primary-foreground min-w-0 line-clamp-2">
                            {masteredOnly
                                ? 'Mastered Phrases'
                                : selectedCollectionFilter
                                    ? `${collections.find(c => c.id === selectedCollectionFilter)?.name || 'Collection'}`
                                    : 'All Phrases'}
                            <span className="text-sm font-normal text-primary-foreground/60 ml-2">
                                ({filteredPhrases.length}{(searchQuery || masteredOnly) ? ` of ${phrases.length}` : ''})
                            </span>
                        </h2>
                        {phrases.length > 0 && (
                            <Button
                                data-testid="toggle-selection-mode"
                                variant="outline"
                                size="sm"
                                className="rounded-full flex-shrink-0 border-0 gap-1 bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"
                                onClick={() => { setIsSelectionMode(!isSelectionMode); setSelectedPhraseIds([]); }}
                            >
                                {isSelectionMode ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                                {isSelectionMode ? 'Cancel' : 'Select'}
                            </Button>
                        )}
                    </div>

                    {/* Selection Controls — visible while in selection mode */}
                    {isSelectionMode && (
                        <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
                            {selectedPhraseIds.length > 0 && (
                                <>
                                    <span className="text-sm text-primary-foreground/70">{selectedPhraseIds.length} selected</span>
                                    <Button
                                        data-testid="bulk-practise-btn"
                                        size="sm"
                                        className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                                        onClick={handlePracticeSelected}
                                    >
                                        <Play className="h-4 w-4 mr-1" />Practise
                                    </Button>
                                    {collections.length > 0 && (
                                        <Button
                                            data-testid="bulk-assign-btn"
                                            variant="outline"
                                            size="sm"
                                            className="rounded-full border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                                            onClick={() => setBulkAssignDialogOpen(true)}
                                        >
                                            <FolderOpen className="h-4 w-4 mr-1" />Assign
                                        </Button>
                                    )}
                                    <Button
                                        data-testid="bulk-delete-btn"
                                        variant="outline"
                                        size="sm"
                                        className="rounded-full border-rose-300 text-rose-300 hover:bg-rose-500 hover:text-white hover:border-rose-500"
                                        onClick={handleBulkDelete}
                                    >
                                        <Trash2 className="h-4 w-4 mr-1" />Delete
                                    </Button>
                                </>
                            )}
                            <Button
                                data-testid="select-all-btn"
                                variant="outline"
                                size="sm"
                                className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                                onClick={toggleSelectAll}
                            >
                                <Check className="h-4 w-4 mr-1" />
                                {filteredPhrases.length > 0 && filteredPhrases.every(p => selectedPhraseIds.includes(p.id)) ? 'Deselect All' : 'Select All'}
                            </Button>
                        </div>
                    )}

                    {masteredOnly && (
                        <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-center gap-2 flex-wrap">
                            <p className="text-sm text-amber-800 flex-1 min-w-0">
                                <Star className="h-4 w-4 inline mr-2 fill-amber-400 text-amber-500" />
                                Phrases averaging {MASTERED_SCORE}% or more.
                                {filteredPhrases.length === 0 && (
                                    <span className="ml-1">None yet, keep practising.</span>
                                )}
                            </p>
                            <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-100 flex-shrink-0"
                                onClick={clearMasteredOnly}
                            >
                                Show all phrases
                            </Button>
                        </div>
                    )}

                    {searchQuery && (
                        <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
                            <p className="text-sm text-primary">
                                <Search className="h-4 w-4 inline mr-2" />
                                Showing {filteredPhrases.length} result{filteredPhrases.length !== 1 ? 's' : ''} for "{searchQuery}"
                                {filteredPhrases.length === 0 && <span className="ml-2">— Try a different search term</span>}
                            </p>
                        </div>
                    )}

                    {phrases.length === 0 ? (
                        <Card className="bg-white border-stone-100 border-dashed">
                            <CardContent className="p-12 text-center">
                                <BookOpen className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-stone-600 mb-2">
                                    {selectedCollectionFilter ? 'No phrases in this collection' : 'No phrases yet'}
                                </h3>
                                <p className="text-stone-500 mb-4">
                                    Add pre-made collections, add phrases manually, let AI generate some for you or import from file
                                </p>
                                <div className="flex flex-wrap gap-3 justify-center">
                                    <Button data-testid="premade-first-btn" className="rounded-full bg-primary" onClick={() => setPremadeDialogOpen(true)}>
                                        <LibraryBig className="h-4 w-4 mr-1" />Add pre-made collections
                                    </Button>
                                    <Button data-testid="add-first-phrase-btn" className="rounded-full bg-primary" onClick={() => setAddDialogOpen(true)}>
                                        <Plus className="h-4 w-4 mr-1" />Add Manually
                                    </Button>
                                    <Button
                                        data-testid="generate-first-phrases-btn"
                                        variant="outline"
                                        className="rounded-full border-primary text-primary"
                                        onClick={() => (isFree ? promptAiUpgrade() : setGenerateDialogOpen(true))}
                                    >
                                        <Sparkles className="h-4 w-4 mr-1" />AI Generate
                                    </Button>
                                    <Button data-testid="import-first-phrases-btn" variant="outline" className="rounded-full border-primary text-primary" onClick={() => setImportDialogOpen(true)}>
                                        <Upload className="h-4 w-4 mr-1" />Import
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ) : filteredPhrases.length === 0 ? (
                        <Card className="bg-white border-stone-100">
                            <CardContent className="p-12 text-center">
                                <Search className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-stone-600 mb-2">No matching phrases</h3>
                                <p className="text-stone-500">Try a different search term or clear the search</p>
                                <Button variant="outline" className="mt-4 rounded-full" onClick={() => setSearchQuery('')}>Clear Search</Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid gap-4 stagger-children">
                            {filteredPhrases.map((phrase) => (
                                <Card
                                    key={phrase.id}
                                    className={`phrase-card bg-white shadow-sm hover:shadow-md ${
                                        isSelectionMode && selectedPhraseIds.includes(phrase.id)
                                            ? 'ring-2 ring-primary border-primary'
                                            : 'border-stone-100'
                                    }`}
                                    style={(!isSelectionMode && newPhraseIds.has(phrase.id)) ? { outline: '2px solid #fbbf24', outlineOffset: '-1px' } : undefined}
                                    data-testid={`phrase-card-${phrase.id}`}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        {newPhraseIds.has(phrase.id) && (
                                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mb-1.5">
                                                                <Sparkles className="h-3 w-3" />
                                                                Check new phrase
                                                            </span>
                                                        )}
                                                        <p className="text-base font-semibold text-foreground leading-snug">{frenchTypography(phrase.french)}</p>
                                                        <p className="text-stone-500 text-sm mb-1.5">{phrase.english}</p>
                                                        {hasPhraseStats(phrase) && (
                                                            /* Even gaps above and below: the English line
                                                               above already carries mb-1.5, so mt-2 makes
                                                               14px there and mb-3.5 matches it before the
                                                               collection badges. */
                                                            <div className="mt-2 mb-3.5">
                                                                <button
                                                                    type="button"
                                                                    data-testid={`toggle-stats-${phrase.id}`}
                                                                    aria-expanded={statsOpenIds.has(phrase.id)}
                                                                    onClick={() => toggleStatsFor(phrase.id)}
                                                                    className="flex items-center gap-1 text-[11px] font-medium text-stone-400 hover:text-stone-600 transition-colors"
                                                                >
                                                                    <BarChart3 className="h-3 w-3" />
                                                                    {statsOpenIds.has(phrase.id) ? 'Hide stats' : 'Stats'}
                                                                    {statsOpenIds.has(phrase.id) ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                                                </button>
                                                                {statsOpenIds.has(phrase.id) && <PhraseStats phrase={phrase} />}
                                                            </div>
                                                        )}
                                                        {phrase.collection_ids && phrase.collection_ids.length > 0 && (
                                                            <div className="flex flex-wrap gap-1">
                                                                {getCollectionNames(phrase.collection_ids).map(name => (
                                                                    <Badge key={name} variant="secondary" className="text-xs">
                                                                        <Folder className="h-3 w-3 mr-1 text-amber-500" />{name}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col items-center gap-0.5 shrink-0 lg:grid lg:grid-cols-2">
                                                        {isSelectionMode ? (
                                                            <Checkbox
                                                                data-testid={`select-phrase-${phrase.id}`}
                                                                checked={selectedPhraseIds.includes(phrase.id)}
                                                                onCheckedChange={() => togglePhraseSelection(phrase.id)}
                                                            />
                                                        ) : (
                                                            <>
                                                                <Button
                                                                    data-testid={`listen-phrase-${phrase.id}`}
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10"
                                                                    disabled={playingPhraseId !== null}
                                                                    aria-label="Listen to phrase"
                                                                    title="Listen"
                                                                    onClick={() => handlePlayPhrase(phrase.id, phrase.french)}
                                                                >
                                                                    {playingPhraseId === phrase.id
                                                                        ? <Loader2 className="h-4 w-4 animate-spin" />
                                                                        : <Volume2 className="h-4 w-4" />}
                                                                </Button>
                                                                <Button
                                                                    data-testid={`practice-phrase-${phrase.id}`}
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                                                                    onClick={() => { sessionStorage.setItem('collections_returning_from_practice', '1'); navigate(`${flowPath(getSavedFlow())}?phrase_id=${phrase.id}&returnTo=collections`); }}
                                                                >
                                                                    <Play className="h-4 w-4 fill-current" />
                                                                </Button>
                                                                <Button
                                                                    data-testid={`edit-phrase-${phrase.id}`}
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 text-stone-400 hover:text-stone-600"
                                                                    onClick={() => { setEditingPhrase({...phrase, collection_ids: phrase.collection_ids || []}); setEditDialogOpen(true); }}
                                                                >
                                                                    <Edit2 className="h-4 w-4" />
                                                                </Button>
<Button
                                                                    data-testid={`delete-phrase-${phrase.id}`}
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                                                                    onClick={() => handleDeletePhrase(phrase.id)}
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" aria-describedby="edit-phrase-description">
                    <DialogHeader><DialogTitle>Edit Phrase</DialogTitle></DialogHeader>
                    <p id="edit-phrase-description" className="text-sm text-stone-500 mb-4">Update your French phrase details and collection assignments.</p>
                    {editingPhrase && (
                        <form onSubmit={handleUpdatePhrase} className="space-y-4">
                            <TooLongWarning text={editingPhrase.french} />
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="edit-french">French Phrase</Label>
                                    {editReverseTranslating && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                                </div>
                                <textarea data-testid="edit-phrase-french" id="edit-french" ref={editFrenchRef} value={editingPhrase.french} onChange={(e) => { handleEditFrenchChange(e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} required rows={1} className="flex w-full rounded-md border border-input bg-stone-50 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none overflow-hidden min-h-[40px] leading-normal" />
                                <SpellingSuggestion suggestion={editFrenchSuggestion} current={editingPhrase.french}
                                    onAccept={(text) => { setEditingPhrase(prev => ({ ...prev, french: text })); setEditFrenchSuggestion(null); }} />
                                {/* Alternatives */}
                                <div>
                                <button
                                    type="button"
                                    className="flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 transition-colors mt-1 font-medium"
                                    onClick={() => showAlternatives ? setShowAlternatives(false) : handleGetAlternatives()}
                                >
                                    <RefreshCw className="h-3 w-3" />
                                    Find alternative phrasings
                                    {showAlternatives ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                </button>
                                {showAlternatives && (
                                    <div className="mt-2 space-y-2">
                                        {loadingAlternatives ? (
                                            <div className="flex items-center gap-2 py-3 text-stone-400">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                <span className="text-sm">Generating alternatives...</span>
                                            </div>
                                        ) : alternatives.map((alt, i) => (
                                            <div key={i} className="p-3 bg-stone-50 border border-stone-100 rounded-lg">
                                                <p className="font-medium text-foreground text-sm leading-snug">{frenchTypography(alt.french)}</p>
                                                <p className="text-xs text-stone-500 mt-0.5">{alt.english}</p>
                                                {alt.phonetic && <p className="text-xs text-stone-400 font-mono mt-0.5">{alt.phonetic}</p>}
                                                <button
                                                    type="button"
                                                    className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                                                    onClick={() => handleSelectAlternative(alt)}
                                                >
                                                    <Check className="h-3 w-3" />
                                                    Use this
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="edit-english">English Translation</Label>
                                    {editTranslating && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                                </div>
                                <textarea data-testid="edit-phrase-english" id="edit-english" ref={editEnglishRef} value={editingPhrase.english} onChange={(e) => { handleEditEnglishChange(e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} required rows={1} className="flex w-full rounded-md border border-input bg-stone-50 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none overflow-hidden min-h-[40px] leading-normal" />
                            </div>
                            <div className="space-y-2">
                                <Label>Collection</Label>
                                <CollectionSelect
                                    testId="edit-collection"
                                    value={editingPhrase.collection_ids?.[0] || ''}
                                    onChange={(id) => setEditingPhrase({ ...editingPhrase, collection_ids: id ? [id] : [] })}
                                    collections={collections}
                                    onCreateCollection={createCollectionToast}
                                />
                            </div>
                            <Button data-testid="update-phrase-btn" type="submit" disabled={editSaving || editTranslating || editReverseTranslating} className="w-full rounded-full bg-primary hover:bg-primary/80">
                                {editSaving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving...</> : 'Update Phrase'}
                            </Button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

{/* Bulk Assign Dialog */}
            <Dialog open={bulkAssignDialogOpen} onOpenChange={setBulkAssignDialogOpen}>
                <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" aria-describedby="bulk-assign-description">
                    <DialogHeader><DialogTitle>Assign to Collections</DialogTitle></DialogHeader>
                    <p id="bulk-assign-description" className="text-sm text-stone-500 mb-4">
                        Select collections for {selectedPhraseIds.length} selected phrase(s). This will replace existing collection assignments.
                    </p>
                    <div className="space-y-4">
                        <Select value={bulkAssignCollectionId || 'none'} onValueChange={(v) => setBulkAssignCollectionId(v === 'none' ? '' : v)}>
                            <SelectTrigger className="bg-stone-50"><SelectValue placeholder="Select a collection..." /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">No collection</SelectItem>
                                <CollectionOptions>
                                    {collections.map(c => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.name} <span className="text-stone-400 text-xs ml-1">({c.phrase_count})</span>
                                        </SelectItem>
                                    ))}
                                </CollectionOptions>
                            </SelectContent>
                        </Select>
                        <div className="flex gap-2">
                            <Button data-testid="bulk-assign-cancel" variant="outline" className="flex-1 rounded-full" onClick={() => setBulkAssignDialogOpen(false)}>Cancel</Button>
                            <Button data-testid="bulk-assign-save" className="flex-1 rounded-full bg-primary hover:bg-primary/80" onClick={handleBulkAssign}>
                                <Check className="h-4 w-4 mr-1" />Apply to {selectedPhraseIds.length} Phrase(s)
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Rename Collection Dialog */}
            <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
                <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" aria-describedby="rename-collection-description">
                    <DialogHeader><DialogTitle>Rename Collection</DialogTitle></DialogHeader>
                    <p id="rename-collection-description" className="text-sm text-stone-500 mb-4">Enter a new name for "{renamingCollection?.name}".</p>
                    <form onSubmit={handleRenameCollection} className="space-y-4">
                        <Input
                            data-testid="rename-collection-input"
                            placeholder="Collection name"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            required
                            autoFocus
                            className="bg-stone-50"
                        />
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="flex-1 rounded-full bg-primary hover:bg-primary/80" disabled={!renameValue.trim()}>Save</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Practice Selection Dialog */}
            <PracticeSessionDialog
                open={practiceDialogOpen}
                onOpenChange={setPracticeDialogOpen}
                collections={collections}
                totalPhrases={stats?.total_phrases || 0}
                selectedPracticeCollections={selectedPracticeCollections}
                setSelectedPracticeCollections={setSelectedPracticeCollections}
                practiceFlow={practiceFlow}
                setPracticeFlow={setPracticeFlow}
                practiceMode={practiceMode}
                setPracticeMode={setPracticeMode}
                onStart={handlePracticeWithSelection}
            />

            {/* Shared upgrade prompt. Driven by whatever blocked the user:
                the server's 402 detail for the phrase cap, our own copy for
                AI Generate. */}
            <PremadeCollectionsDialog
                open={premadeDialogOpen}
                onOpenChange={setPremadeDialogOpen}
                onAdded={fetchData}
                onUpgrade={(detail) => setUpgradePrompt({ title: "You've run out of phrase slots", message: detail })}
            />

            <UpgradeDialog
                open={!!upgradePrompt}
                onOpenChange={(o) => !o && setUpgradePrompt(null)}
                title={upgradePrompt?.title}
                message={upgradePrompt?.message}
            />
        </div>
    );
};

export default Collections;
